import { ChangeDetectionStrategy, Component, ElementRef, inject, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import isEmpty from 'lodash/isEmpty';
import { first } from 'rxjs/operators';

import { ConfigService } from '../../config.service';
import { CoreApiService } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { ProfileService } from '../../profile/profile.service';
import { ContainerType } from '../../shared/enums/container-type.enum';
import { arrayLengthMin, validJson } from '../../shared/form.utils';
import { ViewerApp } from '../../shared/models/site-settings.model';

interface DialogData {
  apps: ViewerApp[];
  appIndex?: number;
  projectId?: string;
}

const containerTypes = Object.values(ContainerType);

@Component({
  host: {
    class: 'mat-dialog-component',
    'test-id': 'app-registration-dialog',
  },
  selector: 'app-registration-dialog',
  styleUrls: ['./app-registration-dialog.component.scss'],
  templateUrl: './app-registration-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AppRegistrationDialogComponent {
  private config = inject(ConfigService);
  private logger = inject(LoggingService);
  private notifications = inject(NotificationService);
  private profileService = inject(ProfileService);
  private coreApi = inject(CoreApiService);
  dialogRef = inject<MatDialogRef<AppRegistrationDialogComponent>>(MatDialogRef);
  data = inject<DialogData>(MAT_DIALOG_DATA);

  @ViewChild('typeInputElement', { static: true }) typeInputElement: ElementRef<HTMLInputElement>;

  private currentUserId: string;

  availableTypes: string[] = [];
  fileTypes: string[] = [];
  suggestedTypes: string[] = [];
  apps: ViewerApp[] = this.data.apps || [];
  app: ViewerApp = this.apps[this.data.appIndex];
  projectId = this.data.projectId;
  form = new FormGroup({
    type: new FormControl(this.app?.type || 'viewer', Validators.required),
    name: new FormControl(this.app?.name || null, Validators.required),
    url: new FormControl(this.app?.url || null, Validators.required),
    description: new FormControl(this.app?.description || null),
    typeInput: new FormControl(),
    types: new FormControl(
      [...Object.keys(this.app?.files || []), ...Object.keys(this.app?.containers || [])].sort((a, b) =>
        a.toLowerCase() < b.toLowerCase() ? -1 : 1,
      ),
      [arrayLengthMin(1)],
    ),
    matchers: new FormGroup(
      Object.assign(
        {},
        ...Object.entries(this.app?.files || {}).map(([typeId, matcher]) => ({
          [typeId]: new FormControl(JSON.stringify(matcher, null, 2), [validJson]),
        })),
        ...Object.entries(this.app?.containers || {}).map(([typeId, matcher]) => ({
          [typeId]: new FormControl(JSON.stringify(matcher, null, 2), [validJson]),
        })),
      ),
    ),
    options: new FormControl(JSON.stringify(this.app?.options || {}, null, 2), [validJson]),
  });
  selectedType: string = null;

  constructor() {
    this.profileService
      .getProfile()
      .pipe(first((profile) => Boolean(profile)))
      .subscribe((profile) => (this.currentUserId = profile._id));

    this.config
      .getFileTypes()
      .pipe(first())
      .subscribe({
        next: (fileTypes) => {
          this.fileTypes = Object.keys(fileTypes);

          this.availableTypes = [...this.fileTypes, ...containerTypes].sort((a, b) =>
            a.toLowerCase() < b.toLowerCase() ? -1 : 1,
          );
        },
        error: (error) => {
          this.logger.error(error);
        },
      });
  }

  isValidType(value): boolean {
    return !this.form.controls.types.value.includes(value) && this.availableTypes?.includes(value);
  }

  selectTypeOption(event: MatAutocompleteSelectedEvent): void {
    const { value } = event.option;
    const { types } = this.form.controls;
    if (!this.isValidType(value)) {
      return;
    }
    const typesValue = [...types.value, value];
    setTimeout(() => {
      // defer this to drop the partial value added by the input blur event
      this.clearChipInput();
      types.setValue(typesValue);
      this.selectTypeChip(value);
    });
  }

  selectTypeChip(value: string): void {
    const matchers = this.form.get('matchers') as FormGroup;
    if (!(value in matchers.controls)) {
      matchers.addControl(value, new FormControl('{}', [validJson]));
    }
    if (this.selectedType === value) {
      this.selectedType = null;
    } else {
      this.selectedType = value;
    }
  }

  removeType(index: number): void {
    const { types } = this.form.controls;
    if (this.selectedType === types.value[index]) {
      this.selectedType = null;
    }
    types.setValue([...types.value.slice(0, index), ...types.value.slice(index + 1)]);
  }

  filterAutocomplete(): void {
    const value = this.form.controls.typeInput.value;
    // case insensitive filter
    this.suggestedTypes = this.availableTypes.filter(
      (availableType) =>
        this.form.controls.types.value.findIndex((v) => v.toLowerCase() === availableType.toLowerCase()) < 0 &&
        (!value || availableType.toLowerCase().includes(value.toLowerCase())),
    );
  }

  clearChipInput(): void {
    this.typeInputElement.nativeElement.value = '';
    this.form.controls.typeInput.reset();
  }

  formatJson(control: AbstractControl): void {
    if (control.valid) {
      control.setValue(JSON.stringify(JSON.parse(control.value), null, 2));
    }
  }

  submit(): void {
    const formData = this.form.value;
    const files = {};
    const containers = {};
    for (const key of formData.types) {
      const matcher = JSON.parse(formData.matchers[key] || '{}');
      if (containerTypes.includes(key as ContainerType)) {
        containers[key] = matcher;
      } else {
        files[key] = matcher;
      }
    }
    const options = JSON.parse(formData.options || '{}');

    const app: ViewerApp = {
      name: formData.name,
      url: formData.url,
      type: formData.type,
      description: formData.description,
    };
    if (this.data.appIndex !== null && this.app) {
      app.id = this.app.id;
    }
    if (!isEmpty(containers)) {
      app.containers = containers;
    }
    if (!isEmpty(files)) {
      app.files = files;
    }
    if (!isEmpty(options)) {
      app.options = options;
    }
    if (!this.app) {
      // Add user info to this new app
      app.origin = { type: 'user', id: this.currentUserId };
    }
    const apps = [...this.apps];
    if (this.app) {
      apps[this.data.appIndex] = app;
    } else {
      apps.push(app);
    }

    if (this.projectId) {
      this.coreApi.projects.settings
        .put(this.projectId, {
          viewer_apps: apps,
        })
        .subscribe({
          next: () => {
            this.notifications.success('viewerApplications.register.success');
            this.dialogRef.close();
          },
          error: (error) => {
            this.logger.error(error);
            this.notifications.error('viewerApplications.register.error');
          },
        });
    } else {
      this.config
        .setSiteSettings({
          viewer_apps: apps,
        })
        .subscribe({
          next: () => {
            this.notifications.success('viewerApplications.register.success');
            this.dialogRef.close();
          },
          error: (error) => {
            this.logger.error(error);
            this.notifications.error('viewerApplications.register.error');
          },
        });
    }
  }
}
